import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import logoIcon from '../../assets/images/logo.png';
import './AdsLandingLayout.css';

const PLATFORMS = ['스마트스토어', '플레이스', '쿠팡'];
const KAKAO_CHAT_URL = 'https://pf.kakao.com/_CLGaX/chat';
const MY_AD_HOME_URL = '/?tab=myAdvertisement';

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.left = '-9999px';
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  document.body.removeChild(area);
};

const useInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
};

const RANK_STEPS = [
  { rank: 155, page: 8, note: '8페이지(시작 전)' },
  { rank: 35, page: 2, note: '2페이지(시작)' },
  { rank: 17, page: 1, note: '1페이지 진입' },
  { rank: 3, page: 1, note: '1페이지 상위권 도달' },
];

const STEP_HOLD_MS = 2600;
const FINAL_HOLD_MS = 6000;
const STEP_OUT_MS = 320;

const useRankStepLoop = (active) => {
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState('in');

  useEffect(() => {
    if (!active) {
      setStep(0);
      setPhase('in');
      return undefined;
    }

    let index = 0;
    let cancelled = false;
    const timers = [];
    setStep(0);
    setPhase('in');

    const scheduleHold = () => {
      const hold = index === RANK_STEPS.length - 1 ? FINAL_HOLD_MS : STEP_HOLD_MS;
      const holdTimer = setTimeout(() => {
        if (cancelled) return;
        setPhase('out');
        const leaveTimer = setTimeout(() => {
          if (cancelled) return;
          index = (index + 1) % RANK_STEPS.length;
          setStep(index);
          setPhase('in');
          scheduleHold();
        }, STEP_OUT_MS);
        timers.push(leaveTimer);
      }, hold);
      timers.push(holdTimer);
    };

    scheduleHold();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [active]);

  return { step, phase, current: RANK_STEPS[step] };
};

const RankRiseVisual = ({ active }) => {
  const { step, phase, current } = useRankStepLoop(active);
  const onFirstPage = current.page === 1;

  return (
    <div className={`sb-ads-rank-stage${active ? ' is-active' : ''}${onFirstPage ? ' is-page1' : ''}`}>
      <div className={`sb-ads-rank-scene is-${phase}`} key={current.rank}>
        <div className="sb-ads-rank-headline">
          <p className="sb-ads-rank-page">{current.note}</p>
        </div>

        <article className="sb-ads-product">
          <div className="sb-ads-product-rank" aria-label={`검색 ${current.rank}위`}>
            <strong>{current.rank}위</strong>
          </div>
          <div className="sb-ads-product-thumb">
            <img
              src="/images/ads-landing/ads_image_1.png"
              alt="롯데칠성음료 펩시제로라임 500ml"
            />
          </div>
          <div className="sb-ads-product-body">
            <button type="button" className="sb-ads-product-heart" aria-label="찜">
              ♡
            </button>
            <h3>롯데칠성음료 펩시제로라임 500ml</h3>
            <p className="sb-ads-product-price">
              <strong>2,400원</strong>
              <span>배송 2,500원</span>
            </p>
            <p className="sb-ads-product-ship">오늘출발 14:00까지 주문 시</p>
            <p className="sb-ads-product-stats">
              ★ 4.63 (1,103) · 구매 577 · 찜 77
            </p>
            <p className="sb-ads-product-seller">
              올려줌 <span>N pay+</span>
            </p>
          </div>
        </article>
      </div>

      <div className="sb-ads-rank-dots" aria-hidden="true">
        {RANK_STEPS.map((item, index) => (
          <span
            key={item.rank}
            className={index === step ? 'is-current' : index < step ? 'is-done' : ''}
          />
        ))}
      </div>
    </div>
  );
};

export const AdsLandingLayout = () => {
  const pageRef = useRef(null);
  const [heroRef, heroIn] = useInView();
  const [painRef, painIn] = useInView();
  const [rankRef, rankIn] = useInView();
  const [formRef, formIn] = useInView();
  const [interest, setInterest] = useState(PLATFORMS[0]);
  const [storeName, setStoreName] = useState('');
  const [phone, setPhone] = useState('');
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);
  const [formError, setFormError] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const [chatBlocked, setChatBlocked] = useState(false);
  const chatUrlRef = useRef('');

  const scrollToForm = () => {
    const root = pageRef.current;
    const target = formRef.current;
    if (!root || !target) return;
    const navH = root.querySelector('.sb-ads-nav')?.offsetHeight ?? 0;
    root.scrollTo({
      top: Math.max(0, target.offsetTop - navH),
      behavior: 'smooth',
    });
  };

  const openKakaoChat = () => {
    if (!chatUrlRef.current) return false;
    const opened = window.open(
      chatUrlRef.current,
      'olzoomKakaoChat',
      'width=480,height=720'
    );
    return Boolean(opened);
  };

  useEffect(() => {
    if (!copyOpen || chatBlocked) return undefined;
    if (secondsLeft === 0) {
      if (openKakaoChat()) {
        setCopyOpen(false);
      } else {
        setChatBlocked(true);
      }
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [copyOpen, secondsLeft, chatBlocked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!storeName.trim() || !phone.trim()) {
      setFormError('스토어명과 휴대폰번호를 입력해주세요.');
      return;
    }
    if (!privacyAgreed) {
      setFormError('개인정보 수집 동의가 필요합니다.');
      return;
    }

    const text = [
      '무료 진단 + 10일 무료 테스트 신청',
      `스토어명: ${storeName.trim()}`,
      `휴대폰번호: ${phone.trim()}`,
      `관심 항목: ${interest}`,
      '개인정보 수집 동의: 동의',
      `마케팅 수신 동의: ${marketingAgreed ? '동의' : '미동의'}`,
    ].join('\n');

    try {
      await copyText(text);
    } catch (copyError) {
      setFormError('신청 내용을 복사하지 못했습니다. 다시 시도해주세요.');
      return;
    }

    chatUrlRef.current = KAKAO_CHAT_URL;
    setFormError('');
    setChatBlocked(false);
    setSecondsLeft(5);
    setCopyOpen(true);
  };

  const closeCopyPopup = () => {
    chatUrlRef.current = '';
    setCopyOpen(false);
  };

  useEffect(() => {
    const root = pageRef.current;
    if (!root) return undefined;

    const sections = () => Array.from(root.querySelectorAll('.sb-ads-section'));
    let locked = false;
    let acc = 0;
    let touchStartY = 0;
    const THRESHOLD = 36;
    const LOCK_MS = 750;

    const getNavHeight = () =>
      root.querySelector('.sb-ads-nav')?.offsetHeight ?? 0;

    const currentIndex = () => {
      const list = sections();
      const navH = getNavHeight();
      const top = root.scrollTop + navH + 8;
      let best = 0;
      let bestDist = Infinity;
      list.forEach((el, i) => {
        const dist = Math.abs(el.offsetTop - top);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    };

    const goTo = (index) => {
      const list = sections();
      const next = Math.max(0, Math.min(list.length - 1, index));
      const target = list[next];
      if (!target) return;
      locked = true;
      acc = 0;
      const navH = getNavHeight();
      const top = next === 0 ? 0 : Math.max(0, target.offsetTop - navH);
      root.scrollTo({ top, behavior: 'smooth' });
      window.setTimeout(() => {
        locked = false;
      }, LOCK_MS);
    };

    const onWheel = (e) => {
      if (locked) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      acc += e.deltaY;
      if (Math.abs(acc) < THRESHOLD) return;
      const dir = acc > 0 ? 1 : -1;
      acc = 0;
      goTo(currentIndex() + dir);
    };

    const onTouchStart = (e) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };

    const onTouchEnd = (e) => {
      if (locked) return;
      const endY = e.changedTouches[0]?.clientY ?? touchStartY;
      const delta = touchStartY - endY;
      if (Math.abs(delta) < THRESHOLD) return;
      goTo(currentIndex() + (delta > 0 ? 1 : -1));
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <div className="sb-ads-page" ref={pageRef}>
      <header className="sb-ads-nav">
        <a href={MY_AD_HOME_URL} className="sb-ads-nav-logo" aria-label="올려줌 홈">
          <img src={logoIcon} alt="올려줌" className="home-logo-img" />
          <span className="sb-ads-nav-tagline">
            <em>상품 순위</em>를 <em>올려주는</em> 셀프마케팅 <em>올려줌</em>
          </span>
        </a>
        <button type="button" onClick={scrollToForm}>
          무료 진단 받기
        </button>
      </header>

      <section
        ref={heroRef}
        className={`sb-ads-section sb-ads-hero${heroIn ? ' is-inview' : ''}`}
      >
        <div className="sb-ads-hero-copy">
          <p className="sb-ads-kicker">순위가 곧 매출</p>
          <h1>
            네이버 쇼핑 매출은
            <br />
            <em>1페이지</em>에서 결정됩니다
          </h1>
          <p className="sb-ads-lead">
            키워드를 검색했을 때 위에 보이는 상품이
            <br className="sb-ads-lead-br-mobile" />
            {' '}
            주문을 가져갑니다.
            <br />
            즉 순위가 곧 매출입니다.
          </p>
          <div className="sb-ads-cta-row">
            <button type="button" className="sb-ads-cta" onClick={scrollToForm}>
              무료 진단 + 10일 무료 테스트
            </button>
          </div>
        </div>
        <RankRiseVisual active={heroIn} />
      </section>

      <section
        ref={painRef}
        className={`sb-ads-section sb-ads-pain${painIn ? ' is-inview' : ''}`}
      >
        <h2>
          순위를 올리려면
          <br />
          <em>유입플</em>과 <em>리워드</em>를 사용합니다
        </h2>
        <p className="sb-ads-pain-lead">
          둘 다 상품 페이지로 들어오는 방문(트래픽)을 만들지만, 만드는 방식이
          다릅니다.
        </p>
        <div className="sb-ads-pain-grid">
          <article>
            <span>유입플</span>
            <h3>프로그램으로 방문을 넣는 방식</h3>
            <p>
              사람이 검색해서 들어온 방문이 아니라, 방문 횟수 자체를 만들어 넣는
              쪽에 가깝습니다.
              <br />
              많이 넣는다고 순위가 오르지도 않고, 최근에는 위험성도 커지고
              있습니다.
            </p>
          </article>
          <article>
            <span>리워드</span>
            <h3>실제 사용자가 방문하는 방식</h3>
            <p>
              리워드 앱에서 사용자가 상품 페이지를 직접 찾아가면 포인트를 받는
              방식입니다.
              <br />
              방문의 주체가 프로그램이 아니라 사람입니다.
            </p>
          </article>
        </div>
      </section>

      <section
        ref={rankRef}
        className={`sb-ads-section sb-ads-stage${rankIn ? ' is-inview' : ''}`}
      >
        <div className="sb-ads-stage-copy">
          <p className="sb-ads-kicker">올려줌의 차이</p>
          <h2>
            올려줌은
            <br />
            <em>리워드</em>로 진행합니다
          </h2>
          <p>
            자사 앱 실제 사용자가 상품 페이지를 방문하는 방식으로
            <br />
            순위를 올리고 유지합니다.
            <br />
            목표 키워드, 하루 유입 수, 진행 기간을 직접 정해 등록할 수 있습니다.
          </p>
        </div>
      </section>

      <section
        ref={formRef}
        className={`sb-ads-section sb-ads-form-wrap${formIn ? ' is-inview' : ''}`}
      >
        <div className="sb-ads-form-head">
          <h2>
            <em>10일 무료 테스트</em>로
            <br />
            지금 경험해 보세요
          </h2>
          <p>
            무료 진단과 함께 10일 무료 테스트를 신청해보세요.
            <br />
            영업일 기준 24시간 내 연락드립니다.
          </p>
        </div>
        <form className="sb-ads-form" onSubmit={handleSubmit}>
          <label>
            스토어명
            <input
              type="text"
              placeholder="올려줌몰"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
            />
          </label>
          <label>
            휴대폰번호
            <input
              type="tel"
              placeholder="010-1234-5678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <div className="sb-ads-interest">
            <span>관심 항목</span>
            <div>
              {PLATFORMS.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={interest === item ? 'is-selected' : ''}
                  onClick={() => setInterest(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
          <label className="sb-ads-check">
            <input
              type="checkbox"
              checked={privacyAgreed}
              onChange={(e) => setPrivacyAgreed(e.target.checked)}
            />
            <span>개인정보 수집 동의</span>
          </label>
          <label className="sb-ads-check">
            <input
              type="checkbox"
              checked={marketingAgreed}
              onChange={(e) => setMarketingAgreed(e.target.checked)}
            />
            <span>마케팅 수신 동의(선택)</span>
          </label>
          <button type="submit" className="sb-ads-cta is-block">
            무료 진단 + 10일 무료 테스트 신청
          </button>
          {formError ? <p className="sb-ads-form-error">{formError}</p> : null}
        </form>
      </section>

      <footer className="sb-ads-footer">
        <strong>올려줌</strong>
        <span>실사용자 리워드로 만드는 검색 순위</span>
      </footer>

      {copyOpen &&
        createPortal(
          <div className="sb-ads-copy-overlay" role="dialog" aria-modal="true">
            <div className="sb-ads-copy-popup">
              {chatBlocked ? (
                <button
                  type="button"
                  className="sb-ads-copy-open"
                  onClick={() => {
                    if (openKakaoChat()) setCopyOpen(false);
                  }}
                >
                  카카오톡 열기
                </button>
              ) : (
                <p className="sb-ads-copy-timer">{secondsLeft}</p>
              )}
              <p>
                내용을 복사했습니다.
                <br />
                카카오톡 채팅창에 붙여넣기 후 전송해 주세요.
              </p>
              <button type="button" className="sb-ads-copy-close" onClick={closeCopyPopup}>
                닫기
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
